<script lang="ts" setup>import { computed as _computed } from 'vue';

import { Spinner, TheAppWrapper } from '@pohlcon/design-system';
import { onBeforeMount, reactive, watch } from 'vue';
import { useRouter } from 'vue-router';

import { useMainStore } from '~/store';

import TheNavBar from '~/components/TheNavBar.vue';
import Header from '~/components/Header.vue';

const store = useMainStore();
const router = useRouter();

const data = reactive({
  hasError: false,
  showLicenses: false
});

const isEmbed = _computed(() =>
  !!router.currentRoute.value.query.embed || false
);

watch(data, () => {
  if (data.hasError) router.push('/error');
});

// isReady should be true if an error occurs, so that the error view can be displayed
const isReady = _computed(() =>
  store.isAppReady || data.hasError);

onBeforeMount(async () => {
  try {
    await store.setData();
  } catch (e) {
    data.hasError = true;
    router.push('/error');
    console.error('error while trying to fetch data: ', e);
  }
});
</script>

<template>
  <div id="toast-container" />
  <TheAppWrapper company="PohlCon" size="medium">
    <div id="app__background" :class="{ embed: isEmbed }">
      <div id="app__background__cover" />
    </div>
    <div id="app__wrapper" :class="{ embed: isEmbed }" class="bootstrap-wrapper">
      <main id="app__content">
        <Header :isEmbed="isEmbed" />
        <TheNavBar />
        <router-view v-if="isReady" :key="$route.fullPath" />
        <div v-else id="loading-data">
          <Spinner /> &nbsp; Loading data...
        </div>
      </main>
    </div>
  </TheAppWrapper>
</template>

<style lang="scss">
@use './_variables' as *;

$app-vertical-padding: var(--gutter-l);
$bg-image: "https://pohlcon.com/typo3temp/assets/_processed_/8/f/csm_REN_PRO_A3DOM_BALKONDAEMMELEMENTE__SALL__AING__V2_efe6f1ffe4.png";

:root {
  --margin-x-container: 0 1rem;
  --hover-background-color: #dbdbdb;
  --hover-opacity: 0.7;
}

#app {
  position: relative;
  min-width: $breakpoint-mobile;

  &__background {
    position: fixed;
    height: 100vh;
    width: 100vw;
    background: url(/assets/app__background.svg) no-repeat center center fixed;
    background-size: cover;
    z-index: 0;

    &__cover {
      background-color: #000;
      opacity: 0.6;
      background-blend-mode: lighten;
      height: 100%;
    }
  }

  &__wrapper {
    --app-width: calc(#{$breakpoint-desktop} * 0.9);

    position: relative;
    padding: $app-vertical-padding 0 var(--gutter-3xl);
    margin: 0 auto;
    width: var(--app-width);
    z-index: 10;

    @media screen and (max-width: $breakpoint-desktop) {
      --app-width: 90vw;
    }

    @media screen and (max-width: $breakpoint-tablet) {
      --app-width: 100%;
      padding: calc($app-vertical-padding/2) 0;
    }

    &.embed {
      margin: 0;
      padding: 0;
      width: 100%;

      #app__content {
        box-shadow: none;
        border-radius: 0px;
      }
    }
  }

  &__content {
    display: grid;
    padding: var(--gutter-2xl) var(--gutter-xl) var(--gutter-l);
    align-items: start;
    border-radius: 5px;
    background-color: var(--color-grey-93);
    // overflow: scroll;

    label.label {
      color: var(--bds-primaryText-color);
    }
  }

  &__background.embed {
    height: auto;

    #app {
      &__wrapper {
        --app-width: 100vw;
        margin: 0;
        padding: 0;
      }

      &__content {
        box-shadow: none;
        border-radius: 0;
        padding: 0;
      }
    }
  }
}

#loading-data {
  text-align: center;
  padding: 30px;
}
</style>
